import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mic, Square, Plus, Plane, Clock } from "lucide-react";

export default function Logbook() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ flight_date: "", aircraft: "C172", departure: "", arrival: "", duration_minutes: 60, notes: "" });
  const [recording, setRecording] = useState(false);
  const [activeEntryId, setActiveEntryId] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { if (!loading && !user) navigate("/", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    api.get("/logbook").then(({ data }) => setEntries(data)).catch(() => {});
  }, [user]);

  const save = async () => {
    if (!form.flight_date || !form.departure || !form.arrival) return toast.error("Fill date, departure, arrival");
    try {
      const { data } = await api.post("/logbook", { ...form, duration_minutes: parseInt(form.duration_minutes) || 0 });
      setEntries((prev) => [data, ...prev]);
      setForm({ flight_date: "", aircraft: "C172", departure: "", arrival: "", duration_minutes: 60, notes: "" });
      toast.success("Flight logged");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const startRecording = async (entryId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, `flight-${entryId}.webm`);
        try {
          const { data } = await api.post(`/logbook/${entryId}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setEntries((prev) => prev.map((e) => e.entry_id === entryId ? { ...e, audio_storage_path: data.audio_url.replace("/api/files/", "") } : e));
          toast.success("Audio uploaded");
        } catch (e) {
          toast.error("Upload failed");
        }
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      setRecording(true);
      setActiveEntryId(entryId);
    } catch (e) {
      toast.error("Microphone permission needed");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current) mediaRef.current.stop();
    setRecording(false);
    setActiveEntryId(null);
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="logbook-page">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Pilot logbook</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Flight log</h1>

      {/* CREATE FORM */}
      <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--brand-primary)] mb-4">New entry</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <Field label="Date">
            <Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} className="bg-white border-slate-300 rounded-sm" data-testid="log-date-input" />
          </Field>
          <Field label="Aircraft">
            <Input value={form.aircraft} onChange={(e) => setForm({ ...form, aircraft: e.target.value })} className="bg-white border-slate-300 rounded-sm" data-testid="log-aircraft-input" />
          </Field>
          <Field label="From (ICAO)">
            <Input value={form.departure} placeholder="KSAN" onChange={(e) => setForm({ ...form, departure: e.target.value.toUpperCase() })} className="bg-white border-slate-300 rounded-sm uppercase font-mono" data-testid="log-from-input" />
          </Field>
          <Field label="To (ICAO)">
            <Input value={form.arrival} placeholder="KMIR" onChange={(e) => setForm({ ...form, arrival: e.target.value.toUpperCase() })} className="bg-white border-slate-300 rounded-sm uppercase font-mono" data-testid="log-to-input" />
          </Field>
          <Field label="Duration (min)">
            <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-white border-slate-300 rounded-sm" data-testid="log-duration-input" />
          </Field>
          <div className="flex items-end">
            <Button onClick={save} className="w-full bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium" data-testid="log-save-btn">
              <Plus className="w-4 h-4 mr-1" /> Log flight
            </Button>
          </div>
        </div>
        <Field label="Notes" className="mt-3">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border-slate-300 rounded-sm" placeholder="Optional notes" data-testid="log-notes-input" />
        </Field>
      </div>

      {/* ENTRIES */}
      <div className="mt-10">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Recorded flights · {entries.length}</div>
        <div className="mt-3 bg-white border border-slate-200 rounded-sm overflow-hidden">
          {entries.length === 0 && (
            <div className="p-12 text-center text-slate-500" data-testid="logbook-empty">
              <Plane className="w-8 h-8 mx-auto mb-3 text-slate-400" />
              No flights logged yet. Log your first flight above.
            </div>
          )}
          {entries.length > 0 && (
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
              <div className="col-span-2">Date</div>
              <div className="col-span-1">A/C</div>
              <div className="col-span-2">Route</div>
              <div className="col-span-1">Time</div>
              <div className="col-span-3">Notes</div>
              <div className="col-span-3 text-right">Audio</div>
            </div>
          )}
          {entries.map((e) => (
            <div key={e.entry_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 border-b border-slate-100 last:border-b-0 items-center" data-testid={`log-entry-${e.entry_id}`}>
              <div className="md:col-span-2 font-mono text-sm text-slate-900">{e.flight_date}</div>
              <div className="md:col-span-1 font-mono text-sm text-slate-700">{e.aircraft}</div>
              <div className="md:col-span-2 font-mono text-sm text-slate-700">{e.departure} → {e.arrival}</div>
              <div className="md:col-span-1 font-mono text-sm text-slate-700 flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-400" />{e.duration_minutes}m
              </div>
              <div className="md:col-span-3 text-sm text-slate-600">{e.notes || <span className="text-slate-400">—</span>}</div>
              <div className="md:col-span-3 flex items-center gap-2 md:justify-end">
                {!e.audio_storage_path && !recording && (
                  <Button onClick={() => startRecording(e.entry_id)} variant="outline" className="border-slate-300 text-slate-700 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] rounded-sm h-8" data-testid={`record-btn-${e.entry_id}`}>
                    <Mic className="w-4 h-4 mr-1" /> Record
                  </Button>
                )}
                {recording && activeEntryId === e.entry_id && (
                  <Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-8" data-testid={`stop-btn-${e.entry_id}`}>
                    <Square className="w-4 h-4 mr-1" /> Stop
                  </Button>
                )}
                {e.audio_storage_path && (
                  <audio controls src={fileUrl(e.audio_storage_path)} className="h-8" data-testid={`audio-${e.entry_id}`} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
